import createUsercryptApi from '@rhg/libs/api/create-usercrypt-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery } from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';
import { useCircle } from '../use-circle';

export const circleConditionsQueryOptions = {
    queryKey: function (circleId: number) {
        return [queryKeys.conditions, circleId.toString()];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        recipientId,
        circleAuthorizationToken,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        recipientId: number;
        circleAuthorizationToken: string;
    }) {
        const usercryptApi = createUsercryptApi(getAccessTokenSilently);

        const response = await usercryptApi.usercryptGetAllConditions({
            recipientId,
            circleAuthorizationToken,
        });

        return response.data;
    },
    queryOptions: function ({
        isAuthenticated,
        recipientId,
        circleAuthorizationToken,
    }: {
        isAuthenticated: boolean;
        recipientId?: number;
        circleAuthorizationToken?: string;
    }) {
        return {
            enabled:
                !!isAuthenticated &&
                !!recipientId &&
                !!circleAuthorizationToken,
        };
    },
};

export function useCircleConditions(circleId: number) {
    const { circle } = useCircle(circleId);
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn, queryOptions } = circleConditionsQueryOptions;

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey(circleId),
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
                recipientId: circle!.recipient?.id,
                circleAuthorizationToken: circle!.authorizationJWT!,
            }),
        ...queryOptions({
            isAuthenticated,
            recipientId: circle?.recipient?.id,
            circleAuthorizationToken: circle?.authorizationJWT,
        }),
    });
    return {
        conditions: data,
        ...otherProps,
    };
}

export function useActiveCircleConditions() {
    const { activeCircleId } = useActiveCircle();

    return useCircleConditions(activeCircleId);
}
